/**
 * useUsers Hook
 * Custom hook for user management state and operations.
 * 
 * Provides:
 * - User list with pagination, filters, and sorting
 * - User statistics
 * - CRUD operations
 * - Optimistic updates
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import userService from '../services/userService';

const DEFAULT_PAGE_SIZE = 10;

export const useUsers = (initialFilters = {}) => {
  // Data state
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  
  // Filter state
  const [filters, setFilters] = useState({
    status: initialFilters.status || '',
    role: initialFilters.role || '',
    department: initialFilters.department || '',
    search: initialFilters.search || '',
    sort_by: initialFilters.sort_by || 'created_at',
    sort_order: initialFilters.sort_order || 'desc',
  });
  
  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Error state
  const [error, setError] = useState(null);
  
  // Fetch users with current filters
  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const params = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== '' && v !== null)
        ),
      };
      
      const response = await userService.listUsers(params);
      setUsers(response.users || []);
      setTotal(response.total || 0);
      setTotalPages(response.total_pages || 0);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.detail || 'Failed to load users');
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, filters]);
  
  // Fetch statistics
  const fetchStats = useCallback(async () => {
    setIsStatsLoading(true);
    
    try {
      const response = await userService.getStats();
      setStats(response);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setIsStatsLoading(false);
    }
  }, []);
  
  // Initial fetch
  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, [fetchUsers, fetchStats]);
  
  // Update filters
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPage(1); // Reset to first page on filter change
  }, []);
  
  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters({
      status: '',
      role: '',
      department: '',
      search: '',
      sort_by: 'created_at',
      sort_order: 'desc',
    });
    setPage(1);
  }, []);
  
  // Sort handler
  const handleSort = useCallback((field) => {
    setFilters(prev => ({
      ...prev,
      sort_by: field,
      sort_order: prev.sort_by === field && prev.sort_order === 'asc' ? 'desc' : 'asc',
    }));
  }, []);
  
  // Invite user
  const inviteUser = useCallback(async (data) => {
    setActionLoading('invite');
    
    try {
      const response = await userService.inviteUser(data);
      toast.success(`Invitation sent to ${data.email}`);
      
      // Refresh data
      await Promise.all([fetchUsers(), fetchStats()]);
      
      return response;
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to send invitation';
      toast.error(message);
      throw err;
    } finally {
      setActionLoading(null);
    }
  }, [fetchUsers, fetchStats]);
  
  // Resend invitation
  const resendInvitation = useCallback(async (userId) => {
    setActionLoading(`resend-${userId}`);
    
    try {
      const response = await userService.resendInvitation(userId);
      toast.success('Invitation resent successfully');
      return response;
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to resend invitation';
      toast.error(message);
      throw err;
    } finally {
      setActionLoading(null);
    }
  }, []);
  
  // Suspend user
  const suspendUser = useCallback(async (userId, reason) => {
    setActionLoading(`suspend-${userId}`);
    
    try {
      const response = await userService.suspendUser(userId, reason);
      toast.success('User suspended');
      
      // Optimistic update
      setUsers(prev => prev.map(u => 
        u.user_id === userId ? { ...u, status: 'suspended' } : u
      ));
      
      // Refresh stats
      await fetchStats();
      
      return response;
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to suspend user';
      toast.error(message);
      throw err;
    } finally {
      setActionLoading(null);
    }
  }, [fetchStats]);
  
  // Reactivate user
  const reactivateUser = useCallback(async (userId) => {
    setActionLoading(`reactivate-${userId}`);
    
    try {
      const response = await userService.reactivateUser(userId);
      toast.success('User reactivated');
      
      // Optimistic update
      setUsers(prev => prev.map(u => 
        u.user_id === userId ? { ...u, status: 'active' } : u
      ));
      
      // Refresh stats
      await fetchStats();
      
      return response;
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to reactivate user';
      toast.error(message);
      throw err;
    } finally {
      setActionLoading(null);
    }
  }, [fetchStats]);
  
  // Unlock user
  const unlockUser = useCallback(async (userId) => {
    setActionLoading(`unlock-${userId}`);
    
    try {
      const response = await userService.unlockUser(userId);
      toast.success('User unlocked');
      
      // Optimistic update
      setUsers(prev => prev.map(u => 
        u.user_id === userId ? { ...u, status: 'active' } : u
      ));
      
      // Refresh stats
      await fetchStats();
      
      return response;
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to unlock user';
      toast.error(message);
      throw err;
    } finally {
      setActionLoading(null);
    }
  }, [fetchStats]);
  
  // Deactivate user
  const deactivateUser = useCallback(async (userId, reason) => {
    setActionLoading(`deactivate-${userId}`);
    
    try {
      const response = await userService.deactivateUser(userId, reason);
      toast.success('User deactivated');
      
      // Refresh data
      await Promise.all([fetchUsers(), fetchStats()]);
      
      return response;
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to deactivate user';
      toast.error(message);
      throw err;
    } finally {
      setActionLoading(null);
    }
  }, [fetchUsers, fetchStats]);
  
  // Reset password
  const resetPassword = useCallback(async (userId, mode, newPassword = null) => {
    setActionLoading(`reset-${userId}`);
    
    try {
      const response = await userService.resetPassword(userId, mode, newPassword);
      toast.success(mode === 'email' ? 'Password reset email sent' : 'Password reset successfully');
      return response;
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to reset password';
      toast.error(message);
      throw err;
    } finally {
      setActionLoading(null);
    }
  }, []);
  
  // Force logout all sessions
  const forceLogoutAll = useCallback(async (userId) => {
    setActionLoading(`logout-${userId}`);
    
    try {
      const response = await userService.forceLogoutAll(userId);
      toast.success(`Terminated ${response.sessions_terminated} sessions`);
      return response;
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to force logout';
      toast.error(message);
      throw err;
    } finally {
      setActionLoading(null);
    }
  }, []);
  
  // Update user
  const updateUser = useCallback(async (userId, data) => {
    setActionLoading(`update-${userId}`);
    
    try {
      const response = await userService.updateUser(userId, data);
      toast.success('User updated');
      
      // Optimistic update
      setUsers(prev => prev.map(u => 
        u.user_id === userId ? { ...u, ...response } : u
      ));
      
      return response;
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to update user';
      toast.error(message);
      throw err;
    } finally {
      setActionLoading(null);
    }
  }, []);
  
  // Get unique departments from users
  const departments = useMemo(() => {
    const depts = new Set(users.map(u => u.department).filter(Boolean));
    return Array.from(depts).sort();
  }, [users]);
  
  // Check if any filter is active
  const hasActiveFilters = useMemo(() => {
    return filters.status || filters.role || filters.department || filters.search;
  }, [filters]);
  
  return {
    // Data
    users,
    stats,
    total,
    totalPages,
    departments,
    
    // Pagination
    page,
    pageSize,
    setPage,
    setPageSize,
    
    // Filters
    filters,
    updateFilters,
    clearFilters,
    hasActiveFilters,
    handleSort,
    
    // Loading
    isLoading,
    isStatsLoading,
    actionLoading,
    
    // Error
    error,
    
    // Actions
    inviteUser,
    resendInvitation,
    suspendUser,
    reactivateUser,
    unlockUser,
    deactivateUser,
    resetPassword,
    forceLogoutAll,
    updateUser,
    
    // Refresh
    refresh: fetchUsers,
    refreshStats: fetchStats,
  };
};

export default useUsers;
